import { Results, Content, Styles, textJustificationTypes } from '../../core/api/ContentTypes';
import { Assay } from '../../core/api/result/ResultType';

const CDC_LINK = {
  badge: 'CDC',
  text: 'Updated resources and symptom information',
  href: 'https://www.cdc.gov/coronavirus/2019-ncov/index.html',
  analyticsId: 'cdc',
};

const FDA_LINK = {
  badge: 'FDA',
  text: 'Patient resources and frequently asked questions',
  href: 'https://www.fda.gov/patients/coronavirus-disease-2019-covid-19-resources-patients',
  analyticsId: 'fda',
};

const IC_FDA_FACT_SHEET_LINK = {
  text: 'FDA Fact Sheet',
  href: 'https://www.fda.gov/media/142595/download',
  analyticsId: 'fda-fact-sheet-ic',
};

const CONTACT_US_EMAIL = 'covidinfo@tempus.com';

const currentContent: Content = {
  version: '2.0.0',
  linkedResources: [CDC_LINK, FDA_LINK],
  contactUsEmail: CONTACT_US_EMAIL,
  requestForPhysicianContactInfoText: 'Help our research by finalizing your consent form',
  consentHeader: [
    {
      paragraph: [
        {
          text:
            'On the next screen, there is an optional consent form that allows Tempus to access medical records from your healthcare provider. This consent is completely optional and not required in order to view your results.',
        },
      ],
    },
  ],
  consentLanguage: [
    {
      paragraph: [
        {
          text: 'Why does Tempus collect data?',
          styles: [Styles.header],
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Tempus is creating a database of de-identified COVID-19 patients in order to advance research and treatment for COVID-19 and other diseases, and we need your help.',
        },
      ],
    },
    { paragraph: [{ text: '' }] },
    { paragraph: [{ text: '' }] },
    {
      paragraph: [
        {
          text: 'What happens to my data?',
          styles: [Styles.header],
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Tempus de-identifies your medical records by removing personal information like your name and date of birth. We may share data only when it has been de-identified, and we will not use your consent for marketing purposes.',
        },
      ],
    },
  ],
  results: {
    [Results.POSITIVE]: {
      disclaimer: [
        {
          paragraph: [
            {
              text:
                'SARS-CoV-2, the virus that causes COVID-19, was detected in your sample. This means it is very likely that you have COVID-19.',
            },
          ],
        },
        {
          paragraph: [
            {
              text: 'For more information, please read the FDA fact sheet below.',
            },
          ],
        },
      ],
      disclaimerResources: [IC_FDA_FACT_SHEET_LINK],
      nextSteps: [
        {
          paragraph: [
            {
              text: 'Please read the FDA fact sheet linked above to learn more about your results.',
            },
          ],
        },
        {
          paragraph: [
            {
              text:
                'Please discuss your test results with your healthcare provider. Together, you can determine the best next steps based on your results, medical history, and symptoms.',
            },
          ],
        },
        {
          paragraph: [
            {
              text:
                'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
            },
          ],
        },
      ],
      nextStepsResources: [CDC_LINK],
    },
    [Results.NEGATIVE]: {
      disclaimer: [
        {
          paragraph: [
            {
              text:
                'SARS-CoV-2, the virus that causes COVID-19, was not detected in your sample. This usually means that COVID-19 did not cause your recent illness. However, it is possible for this test to give a negative result that is incorrect (false negative) in some people with COVID-19.',
            },
          ],
        },
        {
          paragraph: [
            {
              text: 'For more information, please read the FDA fact sheet below.',
            },
          ],
        },
      ],
      disclaimerResources: [IC_FDA_FACT_SHEET_LINK],
      nextSteps: [
        {
          paragraph: [
            {
              text: 'Please read the FDA fact sheet linked above to learn more about your results.',
            },
          ],
        },
        {
          paragraph: [
            {
              text:
                'Please discuss your test results with your healthcare provider. Together, you can determine the best next steps based on your results, medical history, and symptoms.',
            },
          ],
        },
        {
          paragraph: [
            {
              text:
                'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
            },
          ],
        },
      ],
      nextStepsResources: [CDC_LINK],
    },
    [Results.INCONSLUSIVE]: {
      disclaimer: [
        {
          paragraph: [
            {
              text:
                'There was a problem processing your sample. Please contact your healthcare provider for more information and next steps.',
            },
          ],
        },
      ],
      nextSteps: [
        {
          paragraph: [
            {
              text:
                'Please discuss any questions you may have about re-ordering with your healthcare provider. Together, you can determine the best next steps.',
            },
          ],
        },
        {
          paragraph: [
            {
              text:
                'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
            },
          ],
        },
      ],
      nextStepsResources: [CDC_LINK],
    },
    [Results.ERROR]: {
      disclaimer: [
        {
          paragraph: [
            {
              text:
                'There was a problem rendering your results. Please contact your healthcare provider for more information.',
            },
          ],
        },
      ],
      nextSteps: [
        {
          paragraph: [
            {
              text: 'Please contact your healthcare provider to determine the best next steps.',
            },
          ],
        },
        {
          paragraph: [
            {
              text: 'Tempus can be reached at ',
            },
            {
              text: CONTACT_US_EMAIL,
              selectable: true,
            },
            {
              text: ' to assist with any technical issues',
            },
          ],
        },
      ],
      nextStepsResources: [],
    },
  },
  resultsV2: {
    [Assay.SarsCov2]: {
      [Results.POSITIVE]: {
        disclaimer: [
          {
            paragraph: [
              {
                text:
                  'SARS-CoV-2, the virus that causes COVID-19, was detected in your sample. This means it is very likely that you have COVID-19.',
              },
            ],
          },
          {
            paragraph: [
              {
                text: 'For more information, please read the FDA fact sheet below.',
              },
            ],
          },
        ],
        disclaimerResources: [IC_FDA_FACT_SHEET_LINK],
        nextSteps: [
          {
            paragraph: [
              {
                text: 'Please read the FDA fact sheet linked above to learn more about your results.',
              },
            ],
          },
          {
            paragraph: [
              {
                text:
                  'Please discuss your test results with your healthcare provider. Together, you can determine the best next steps based on your results, medical history, and symptoms.',
              },
            ],
          },
          {
            paragraph: [
              {
                text:
                  'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
              },
            ],
          },
        ],
        nextStepsResources: [CDC_LINK],
      },
      [Results.NEGATIVE]: {
        disclaimer: [
          {
            paragraph: [
              {
                text:
                  'SARS-CoV-2, the virus that causes COVID-19, was not detected in your sample. This usually means that COVID-19 did not cause your recent illness. However, it is possible for this test to give a negative result that is incorrect (false negative) in some people with COVID-19.',
              },
            ],
          },
          {
            paragraph: [
              {
                text: 'For more information, please read the FDA fact sheet below.',
              },
            ],
          },
        ],
        disclaimerResources: [IC_FDA_FACT_SHEET_LINK],
        nextSteps: [
          {
            paragraph: [
              {
                text: 'Please read the FDA fact sheet linked above to learn more about your results.',
              },
            ],
          },
          {
            paragraph: [
              {
                text:
                  'Please discuss your test results with your healthcare provider. Together, you can determine the best next steps based on your results, medical history, and symptoms.',
              },
            ],
          },
          {
            paragraph: [
              {
                text:
                  'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
              },
            ],
          },
        ],
        nextStepsResources: [CDC_LINK],
      },
      [Results.INCONSLUSIVE]: {
        disclaimer: [
          {
            paragraph: [
              {
                text:
                  'There was a problem processing your sample. Please contact your healthcare provider for more information and next steps.',
              },
            ],
          },
        ],
        nextSteps: [
          {
            paragraph: [
              {
                text:
                  'Please discuss any questions you may have about re-ordering with your healthcare provider. Together, you can determine the best next steps.',
              },
            ],
          },
          {
            paragraph: [
              {
                text:
                  'You can visit the CDC website for updated COVID-19 information, recommendations on how to stay safe, and what to do if you are experiencing symptoms.',
              },
            ],
          },
        ],
        nextStepsResources: [CDC_LINK],
      },
      [Results.ERROR]: {
        disclaimer: [
          {
            paragraph: [
              {
                text:
                  'There was a problem rendering your results. Please contact your healthcare provider for more information.',
              },
            ],
          },
        ],
        nextSteps: [
          {
            paragraph: [
              {
                text: 'Please contact your healthcare provider to determine the best next steps.',
              },
            ],
          },
          {
            paragraph: [
              {
                text: 'Tempus can be reached at ',
              },
              {
                text: CONTACT_US_EMAIL,
                selectable: true,
              },
              {
                text: ' to assist with any technical issues',
              },
            ],
          },
        ],
        nextStepsResources: [],
      },
    },
  },
  termsAndConditions: [
    {
      paragraph: [
        {
          text: 'Tempus Labs, Inc.',
          styles: [Styles.bold, Styles.header],
        },
      ],
      singleSpace: true,
    },
    {
      paragraph: [
        {
          text: 'Terms and Conditions of Use',
          styles: [Styles.bold, Styles.header],
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'Effective',
          styles: [Styles.bold],
        },
        {
          text: ': August 5, 2020',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'These Terms and Conditions of Use (“',
        },
        {
          text: 'Terms',
          styles: [Styles.bold],
        },
        {
          text: '”) are a contract between users (“',
        },
        {
          text: 'you',
          styles: [Styles.bold],
        },
        {
          text: '”) and Tempus Labs, Inc., (collectively, “',
        },
        {
          text: 'Tempus',
          styles: [Styles.bold],
        },
        {
          text: '”, “',
        },
        {
          text: 'we',
          styles: [Styles.bold],
        },
        {
          text: '”, “',
        },
        {
          text: 'us',
          styles: [Styles.bold],
        },
        {
          text: '” or "',
        },
        {
          text: 'our',
          styles: [Styles.bold],
        },
        { text: '”). These Terms apply to our mobile application for reporting of laboratory test results (“' },
        {
          text: 'The App',
          styles: [Styles.bold],
        },
        { text: '”). The App allows you to access your laboratory test results (“' },
        {
          text: 'Test Results',
          styles: [Styles.bold],
        },
        { text: '”), among other services (the foregoing, collectively “' },
        {
          text: 'Services',
          styles: [Styles.bold],
        },
        { text: '”).' },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Please read and review these Terms carefully before accessing or using The App or the Services. You do not need to use The App to access the Test Results. BY AGREEING TO THESE TERMS, YOU ACKNOWLEDGE THAT (A) YOU HAVE READ AND UNDERSTAND THESE TERMS; AND (B) YOU ACCEPT THESE TERMS AND AGREE THAT YOU ARE LEGALLY BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE APP OR SERVICES.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'Important Notices:',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'The Services are not an attempt to practice medicine or provide specific medical advice. For health care or answers to your personal questions, please consult with your physician. The Services should not be used as a substitute for direct care.',
        },
      ],
      bulletPoint: true,
    },
    {
      paragraph: [
        {
          text:
            'Do not delay or forgo seeking medical care from your physician if you have questions, concerns,or symptoms related to health topics or information that may be presented or referenced on the Services.',
        },
      ],
      bulletPoint: true,
    },

    {
      paragraph: [
        {
          text:
            'Do not delay or forgo seeking medical care from your physician if you have questions, concerns, or symptoms related to health topics or information that may be presented or referenced on the Services.',
        },
      ],
      bulletPoint: true,
    },

    {
      paragraph: [
        {
          text:
            'We do not knowingly collect, maintain, or use User Information provided through The App from individuals under the age of majority in their state (which is 18 in most states, but in some states is 19). You should not use The App if you are under the age of majority in your state. If you learn that person under the age of majority provided us with information, you may alert us at ',
        },
        {
          text: 'support@tempus.com',
          selectable: true,
        },
        {
          text:
            '. If we learn that we have collected any User Information from such an individual, we will delete such information upon request (unless prohibited from doing so by applicable law) and terminate the individual’s account with reasonable promptness.',
        },
      ],
      bulletPoint: true,
    },

    {
      paragraph: [
        {
          text:
            'Section 17 of these Terms contains a mandatory arbitration provision that requires the use of arbitration on an individual basis and limits the remedies available to you in the event of certain disputes relating to the operation or use of The App.',
        },
      ],
      bulletPoint: true,
    },
    {
      paragraph: [
        {
          text: '1.\t',
        },
        {
          text: 'Permissions We Give You',
          styles: [Styles.underline],
        },
        {
          text:
            ': We grant you a non-exclusive, non-sublicensable, non-transferable, revocable license to use and download one copy of The App and access the Services in accordance with these Terms. These Terms will govern any content, materials, or services accessible from within The App, as well as modifications (including upgrades or new versions) provided by Tempus that replace or supplement the original version of The App. You may not copy, reverse-engineer, disassemble, attempt to derive the source code of, modify, distribute, display, or create derivative works of The App, any modification, or any part thereof. We reserve all rights in and to The App and the Services that are not expressly granted to you under the Terms.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '2.\t',
        },
        {
          text: 'No Patient or Client Relationship',
          styles: [Styles.underline],
        },
        {
          text:
            ': You direct Tempus to disclose the Test Results to you through The App. Your use of The App or the Services does not create a patient or client relationship between you and Tempus.  The Services provided through The App are intended to facilitate communications between you and Tempus.  IF YOU THINK YOU MAY HAVE A MEDICAL EMERGENCY, DO NOT USE THE SERVICES TO CONTACT YOUR HEALTHCARE PROVIDER.  PLEASE CALL 911 OR SEEK MEDICAL ATTENTION AT YOUR NEAREST EMERGENCY DEPARTMENT IMMEDIATELY.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '3.\t',
        },
        {
          text: 'United States Residents Only',
          styles: [Styles.underline],
        },
        {
          text:
            'The App is intended for use by individuals located in the United States, and is not intended for use by anyone located outside of the United States. You should not use The App if you are not located in the United States.  Tempus makes no claims that The App or the Services is accessible or appropriate outside of the United States.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '4.\t',
        },
        {
          text: 'Privacy Policies.',
          styles: [Styles.underline],
        },
        {
          text: 'Our Privacy Policy, available online at ',
        },
        {
          text: 'https://www.tempus.com/privacypolicy/',
          selectable: true,
        },
        {
          text: ', and our Notice of Privacy Practices, available online at ',
        },

        {
          text: 'https://www.tempus.com/notice-of-privacy-practices/',
          selectable: true,
        },

        {
          text: ', or such other locations as we may make available (collectively, “',
        },
        { text: 'Privacy Policies', styles: [Styles.bold] },
        {
          text:
            '”), explain how we collect and use your User Information (defined below). We reserve the right to modify our Privacy Policies from time to time.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '5.\t',
        },
        {
          text: 'Permissions You Give Us',
          styles: [Styles.underline],
        },
        {
          text:
            ': Through your use of The App or the Services, you may provide personal information, health information, consent forms, agreements, requests, comments, ideas, suggestions, information, data, files, videos, images or other materials to us or to The App (“',
        },

        { text: 'User Information', styles: [Styles.bold] },
        {
          text:
            '”). You agree that Tempus may collect and use User Information, device and usage data, and technical data to facilitate the provision of the Services, software updates, product support, and other services for you. You grant Tempus, Tempus Affiliates, and contractors a non-exclusive, royalty-free, irrevocable license to collect, use, and process such User Information, device and usage data, and technical data to provide and support the Services and improve our products and offerings, to the extent permitted by applicable law.  Some of the information you provide through use of The App may be considered protected health information (“',
        },
        { text: 'PHI', styles: [Styles.bold] },
        {
          text:
            '”) when held by Tempus or its business associates.  PHI includes demographic and contact information (name, mailing address, email address, gender, age) and information about your health.  You agree that Tempus’ use and disclosure of your PHI will be governed by the Tempus Notice of Privacy Practices, available at the link above.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Tempus may de-identify User Information such that it is no longer considered PHI or personally identifiable information. Tempus may disclose, aggregate, sell, or otherwise use such de-identified information with third parties for analytics, research, or other purposes and such information is no longer subject to the Tempus Notice of Privacy Practices.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'You agree not to contact other The App users through unsolicited email, telephone calls, mailings or any other method of communication. You represent and warrant to Tempus that you have the legal right and authorization to provide all User Information to Tempus for use as set forth herein and required by Tempus.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            "As part of using The App, you are required to provide us with a valid email address and to update your email address if it changes. By creating an account with us through The App, you also consent to receive electronic communications from Tempus (e.g., via email, text message, or by posting notices to the Services). These communications may include operational notices about your account (e.g., payment authorizations, password changes and other transactional information) and are part of your relationship with us. When you use The App or the Services, or send e-mails, messages, and other communications from your desktop or mobile device to us through The App or otherwise, you are communicating with us electronically. Any messages you send to us through The App may be routed to an appropriate person for handling, and someone other than the patient's physician may be involved in addressing requests. The message and response may become part of the patient medical record. You consent to receive communications from us electronically. Before sending certain types of electronic communications to you we may seek additional consent from you, and the terms of that additional consent will govern such communications. You agree that (a) all agreements and consents can be signed electronically, and (b) all notices, disclosures, and other communications that we provide to you electronically satisfy any legal requirement that such notices and other communications be in writing.  Furthermore, we are only able to respond to electronic communications you send us based on the information you provide. So if you provide insufficient or inaccurate information, we will not be able to provide accurate and reliable information in response.",
        },
      ],
    },
    {
      paragraph: [
        {
          text: '6.\t',
        },
        {
          text: 'Acceptable Use',
          styles: [Styles.underline],
        },
        {
          text:
            '. You agree not to (i) access or use The App or the Services in any unlawful way or for any unlawful purpose; (ii) post or transmit (a) a message under a false name, or (b) any data, materials, content, User Information or information (including, without limitation, advice, and recommendations) (collectively “',
        },
        { text: 'Information', styles: [Styles.bold] },
        {
          text:
            '”) which is (1) libelous, defamatory, obscene, fraudulent, false, harassing, pornographic, harmful, or contrary to the ownership or intellectual property rights of any other person, or (2) contains or promotes any virus, worm, Trojan horse, time bomb, malware, or other computer programing or code that is designed or intended to damage, destroy, intercept, download, interfere, manipulate, or otherwise interrupt or expropriate The App, personal information, software, equipment, servers, or Information or facilitate or promote hacking or similar conduct; (iii) impersonate or misrepresent your identity or falsely state or misrepresent your affiliation with a person or entity; (iv) tamper, hack, spoof, copy, modify, or otherwise corrupt the administration, security, or proper function of The App or the Services; (v) use robots or scripts with The App; (vi) attempt to reverse engineer, reverse assemble, reverse compile, decompile, disassemble, translate, or otherwise alter, defraud, or create false results from any executable code, information on, or received by The App or the Services; (vii) to have any antivirus or antispyware software running that is set to override the internet browser’s cookies setting; (viii) incorrectly identify the sender of any message transmitted to The App or via the Services; (viii) attempt to probe, scan or test the vulnerability of a system or network or to beach security or authentication measures without proper authorization; or (ix) use or reference The App for benchmarking or to create any similar or competitive product. You may not alter the attribution or origin of electronic mail, messages, or posting; harvest or collect protected health information about any other individual who uses The App or the Services; or infringe or facilitate infringement on any copyright, patent, trademark, trade secret, or other proprietary, publicity, or privacy rights of any party, including such rights of third parties.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '7.\t',
        },
        {
          text: 'Securing Your Account',
          styles: [Styles.underline],
        },
        {
          text:
            '. User names and passwords provide two layers of authentication. As a The App user, your role in maintaining the security of your information is: (i) using a strong password, (ii) changing your password on a regular basis, and (iii) keeping your login ID and password confidential and not sharing it with any other person. If you share your The App ID and password with another person, that person may be able to view your information, which may include health information.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '8.\t',
        },
        {
          text: 'Indemnification',
          styles: [Styles.underline],
        },
        {
          text:
            '. You agree to defend, indemnify, and hold harmless Tempus and any Tempus Affiliates and their respective trustees, directors, medical staff, employees, contractors, agents, successors and assigns from and against any and all rights, demands, losses, liabilities, damages, claims, causes of action, actions, and suits (no matter whether at law or equity), fees, costs, and attorney’s fees of any kind whatsoever arising directly or indirectly out of or in connection with: (i) your use or misuse of The App, the Services, or any other information posted on The App; (ii) your breach of these Terms or the Privacy Policies; (iii) the content or subject matter of any User Information you provide to Tempus or a customer service agent; (iv) any negligent or wrongful act or omission by you in your use or misuse of The App or any Information on The App; (v) or any User Information that you upload to or transmit through The App, including without limitation, infringement of third party intellectual property rights, privacy rights, or negligent or wrongful conduct. To the extent required by these Terms, Tempus will investigate, defend, settle, and discharge any and all third-party claims, judgments or causes of action that allege that The App infringes: (a) any U.S. patents; (b) U.S. copyrights; (c) U.S. trade secrets; or (d) other U.S. intellectual property rights of any third party. Should The App become the subject of an infringement claim, Tempus may, at its sole discretion replace, substitute, or terminate your use of the software that is the subject to such claim. Tempus will have no obligation for any infringement claim that results from your alteration or modification of The App in a manner not expressly permitted by these Terms, or use of The App for any purpose other than for which it is intended.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '9.\t',
        },
        {
          text: 'Claims of Copyright Infringement',
          styles: [Styles.underline],
        },
        {
          text:
            '. Tempus respects the intellectual property rights of others and expects its users to do the same. In accordance with the Digital Millennium Copyright Act (“',
        },
        { text: 'DMCA', styles: [Styles.bold] },
        {
          text:
            '”), we will respond promptly to notices of alleged infringement that are reported to Tempus’ Designated Copyright Agent, identified below. We reserve the right to delete or disable content alleged to be infringing and terminate accounts of repeat infringers. If you are a copyright owner, authorized to act on behalf of one, or authorized to act under any exclusive right under copyright, please report alleged copyright infringements taking place on or through The App by sending us a notice (“',
        },
        { text: 'Notice', styles: [Styles.bold] },
        {
          text: '”) complying with the following requirements:',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'Identify the copyrighted works that you claim have been infringed;',
        },
      ],
      letterBulletPoint: 'a',
    },

    {
      paragraph: [
        {
          text:
            'Identify the material you claim is infringing (or the subject of infringing activity) and that access to which is to be disabled, including at a minimum, if applicable, a description of where such material may be found on The App;',
        },
      ],
      letterBulletPoint: 'b',
    },

    {
      paragraph: [
        {
          text: 'Provide your mailing address, telephone number, and, if available, email address;',
        },
      ],
      letterBulletPoint: 'c',
    },

    {
      paragraph: [
        {
          text: 'Include both of the following statements in the body of the Notice:',
        },
      ],
      letterBulletPoint: 'd',
    },

    {
      paragraph: [
        {
          text:
            '“I hereby state that I have a good faith belief that the disputed use of the copyrighted material is not authorized by the copyright owner, its agent, or the law (e.g., as a fair use).”',
        },
      ],
      indent: 50,
    },

    {
      paragraph: [
        {
          text:
            '“I hereby state that the information in this Notice is accurate and, under penalty of perjury, that I am the owner, or authorized to act on behalf of the owner, of the copyright or of an exclusive right under the copyright that is allegedly infringed.”; and',
        },
      ],
      indent: 50,
    },

    {
      paragraph: [
        {
          text: 'Provide your full legal name and your electronic or physical signature.',
        },
      ],
      letterBulletPoint: 'e',
    },
    {
      paragraph: [
        {
          text: '\tDeliver this Notice, with all items completed, to our Copyright Agent:',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'Tempus Labs, Inc.',
        },
      ],
      justify: textJustificationTypes.CENTER,
    },
    {
      paragraph: [
        {
          text: 'Attn: Legal Department',
        },
      ],
      justify: textJustificationTypes.CENTER,
    },
    {
      paragraph: [
        {
          text: '600 W. Chicago Ave.',
        },
      ],
      justify: textJustificationTypes.CENTER,
    },
    {
      paragraph: [
        {
          text: 'Chicago, IL 60654',
        },
      ],
      justify: textJustificationTypes.CENTER,
    },
    {
      paragraph: [
        {
          text: '10.\t',
        },
        {
          text: 'Claims of Copyright Infringement',
          styles: [Styles.underline],
        },
        {
          text:
            '. The App may enable access to or require use of Tempus’ or a third party’s services and websites (collectively, “',
        },
        { text: 'External Services', styles: [Styles.bold] },
        {
          text:
            '”). You agree that use of the External Services is at your sole risk. Tempus is not responsible for examining or evaluating the content or accuracy of any External Services, and shall not be liable for any such External Services. Data made available by The App or any External Service, including but not limited to financial, medical and location information, is for general informational purposes only and is not guaranteed by Tempus or its agents. External Services may not be available in all languages or in your home country, and may not be appropriate or available for use in any particular location. To the extent you choose to use External Services, you are solely responsible for compliance with any laws applicable to such External Services or their use. Tempus reserves the right to change, suspend, remove, disable or impose access restrictions or limits on any External Services at any time without notice or liability to you.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '11.\t',
        },
        {
          text: 'Termination',
          styles: [Styles.underline],
        },
        {
          text:
            '. The Terms are effective for so long as you are using or accessing The App or the Services unless and until terminated by Tempus. Your rights under these Terms will terminate automatically if you fail to comply with them. We reserve the right to terminate and/or suspend your access to The App or the Services at any time for any reason. Sections 2, 5, 6, 8, 10, 13-15, 17-19, 20, 22, and 25-26 survive the termination or expiration of the Terms.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '12.\t',
        },
        {
          text: 'Modification of The App or the Services',
          styles: [Styles.underline],
        },
        {
          text:
            '. Tempus reserves complete and sole discretion with respect to the operation of The App and the Services. We may withdraw, suspend, or discontinue any functionality or feature of The App or the Services among other things. We are not responsible for transmission errors, corruption, or compromise of information carried over local or interchange telecommunications carrier. We are not responsible for maintaining information arising from use of The App or with respect to the Services. We reserve the right to maintain, delete, or destroy all communications or information posted or uploaded to The App or the Services in accordance with our internal record retention and/or destruction policies.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '13.\t',
        },
        {
          text: 'Disclaimer of Warranties',
          styles: [Styles.underline],
        },
        {
          text:
            '. TEMPUS AND ITS RESPECTIVE SUPPLIERS DISCLAIM ALL EXPRESS AND IMPLIED WARRANTIES WITH REGARD TO THE CONTENT, INFORMATION, SERVICES, AND MATERIALS CONTAINED ON OR MADE AVAILABLE THROUGH THE APP, INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. ACCESS TO THE APP MAY BE INTERRUPTED. ALTHOUGH TEMPUS ATTEMPTS TO ENSURE THE INTEGRITY AND ACCURATENESS OF THE APP AND THE SERVICE DESCRIPTIONS, IT MAKES NO REPRESENTATIONS, WARRANTIES, OR GUARANTEES WHATSOEVER AS TO THE CORRECTNESS OR ACCURACY OF THE APP, SERVICES, MATERIALS AND OTHER CONTENT ON OR MADE AVAILABLE THROUGH THE APP. INFORMATION CONTAINED ON THE APP MAY BE CHANGED WITHOUT NOTICE. TEMPUS SHALL HAVE NO RESPONSIBILITY OR LIABILITY FOR INFORMATION OR CONTENT ON OR MADE AVAILABLE THROUGH THE APP FROM ANY NON-TEMPUS-AFFILIATED THIRD PARTY AND/OR FOR LOSS OR DAMAGE TO DATA, INCLUDING USER INFORMATION.',
        },
        {
          text:
            'YOU ASSUME TOTAL RESPONSIBILITY AND RISK FOR YOUR USE OF THE APP, THE SERVICES, AND LINKED WEBSITES AND APPLICATIONS. TEMPUS DOES NOT WARRANT THAT FILES AVAILABLE FOR DOWNLOAD WILL BE FREE OF VIRUSES, WORMS, TROJAN HORSES, OR OTHER DESTRUCTIVE PROGRAMMING. YOU ARE RESPONSIBLE FOR IMPLEMENTING PROCEDURES SUFFICIENT TO SATISFY YOUR NEEDS FOR DATA BACK UP AND SECURITY.',
        },
        {
          text:
            'WARRANTIES RELATING TO PRODUCTS OR SERVICES OFFERED, SOLD AND DISTRIBUTED BY TEMPUS MAY BE SUBJECT TO SEPARATE WARRANTY TERMS AND CONDITIONS, IF ANY, PROVIDED BY TEMPUS OR THIRD PARTIES WITH OR IN CONNECTION TO THE APPLICABLE PRODUCTS OR SERVICES. YOU ACKNOWLEDGE AND UNDERSTAND THAT THE USE OR MISUSE OF THE PRODUCTS OR SERVICES OBTAINED THROUGH THE APP MAY RESULT IN UNDESIRABLE OR UNEXPECTED CONSEQUENCES.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '14.\t',
        },
        {
          text: 'Limitation of Liability',
          styles: [Styles.underline],
        },
        {
          text:
            '. TEMPUS DOES NOT ACCEPT ANY LIABILITY FOR THE CONSEQUENCES ARISING FROM THE APPLICATION, USE, OR MISUSE OF ANY PRODUCTS, SERVICES, MATERIALS, OR CONTENT CONTAINED ON OR MADE AVAILABLE THROUGH THE APP OR THE SERVICES.',
        },
        {
          text:
            'NEITHER TEMPUS NOR ANYONE ELSE INVOLVED IN CREATING, PRODUCING, OR DELIVERING THE APP OR THE INFORMATION, SERVICES, AND CONTENT CONTAINED ON OR MADE AVAILABLE THROUGH THE APP ASSUMES ANY LIABILITY OR RESPONSIBILITY FOR THE ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY INFORMATION, SERVICES, OR CONTENT ON OR MADE AVAILABLE THROUGH THE APP. ALL INFORMATION, SERVICES, AND MATERIALS ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED.',
        },
        {
          text:
            'IN NO EVENT SHALL TEMPUS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, EXEMPLARY, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY DAMAGES WHATSOEVER, RESULTING FROM THE USE OF, OR INABILITY TO USE, THE APP, THE SERVICES AND/OR THE INFORMATION, SERVICES, OR CONTENT ON THE APP, WHETHER BASED ON BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, OR OTHERWISE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.',
        },
        {
          text:
            'YOUR SOLE REMEDY FOR USE OF THE APP, THE SERVICES, OR LINKED WEBSITES AND APPLICATIONS IS TO STOP USE.',
        },
        {
          text:
            'BY USING THE APP, YOU ACKNOWLEDGE AND AGREE THAT THE LIMITATIONS SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF THESE TERMS & CONDITIONS OF USE AND THE APP WOULD NOT BE PROVIDED TO YOU WITHOUT THOSE LIMITATIONS.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '15.\t',
        },
        {
          text: 'No Liability for Select Third Parties',
          styles: [Styles.underline],
        },
        {
          text:
            '. The operator of any application store, marketplace, or similar service through which you obtain The App, and their respective affiliates, suppliers, and licensors (collectively, the “',
        },
        {
          text: 'Select Third Parties',
          styles: [Styles.bold],
        },
        {
          text:
            '”) are not parties to these Terms and do not own and are not responsible for The App. You are responsible for complying with all application store and other applicable Select Third Parties’ terms and conditions.  YOU AGREE (A) THE SELECT THIRD PARTIES DISCLAIM ALL WARRANTIES, EXPRESS AND IMPLIED, WITH RESPECT TO THE APP, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF NON-INFRINGEMENT, TITLE, MERCHANTABILITY, QUIET ENJOYMENT, QUALITY OF INFORMATION, AND FITNESS FOR A PARTICULAR PURPOSE; (B) IN NO EVENT WILL THE SELECT THIRD PARTIES BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, PUNITIVE, EXEMPLARY, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES (WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE) ARISING OUT OF THESE TERMS & CONDITIONS OF USE OR THE APP, EVEN IF THEY HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES OR LOSSES; AND (C) YOU WAIVE ANY AND ALL CLAIMS, NOW KNOWN OR LATER DISCOVERED, THAT YOU MAY HAVE AGAINST THE SELECT THIRD PARTIES ARISING OUT OF THE APP AND THESE TERMS & CONDITIONS OF USE.  SELECT THIRD PARTIES ARE INTENDED THIRD PARTY BENEFICIARIES OF THESE TERMS & CONDITIONS OF USE, AND ARE CAPABLE OF DIRECTLY ENFORCING ITS TERMS. NOTHING CONTAINED IN THESE TERMS & CONDITIONS OF USE WILL BE CONSTRUED AS MODIFYING OR AMENDING ANY AGREEMENTS OR OTHER TERMS BETWEEN YOU AND THE SELECT THIRD-PARTIES WITH REGARD TO THEIR SUBJECT MATTER.  In the event of any claim that THE APP or your use of the Services infringes a third party’s intellectual property rights, the Select Third Parties are not responsible for the investigation, defense, settlement, or discharge of the infringement claim.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '16.\t',
        },
        {
          text: 'If you access The App via a mobile device or tablet branded by Apple, Inc. ("',
        },
        {
          text: 'Apple',
          styles: [Styles.bold],
        },
        {
          text: `") and running Apple's iOS (an "`,
        },
        {
          text: 'Apple Device',
          styles: [Styles.bold],
        },
        {
          text: '"), the following terms ("',
        },
        {
          text: 'Apple Terms',
          styles: [Styles.bold],
        },
        {
          text: '") are hereby made part of these Terms:',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'If these Apple Terms conflict with any other provision of these Terms, then the Apple Terms control with respect to access and use of the Services via an Apple Device. Apple is not a party to these Terms and does not own and is not responsible for any App you access via your Apple Device ("',
        },
        {
          text: 'iOS App',
          styles: [Styles.bold],
        },
        {
          text: '") and the content thereof. The Apple App Store Terms of Service (the "',
        },
        {
          text: 'App Store Terms of Service',
          styles: [Styles.bold],
        },
        {
          text: `") control to the extent of any conflict with these Terms with respect to an iOS App. The license granted to use an iOS App is limited to a non-transferable license to use the App on an Apple Device that you own or control and as permitted by the usage rules set forth in the App Store Terms of Service. Apple has no obligation whatsoever to furnish any maintenance and support services with respect to any iOS App. In the event of any failure of the iOS App to conform to any applicable warranty provided by Tempus in these Terms, you may notify Apple and Apple will refund the purchase price for the iOS App (if any). To the maximum extent permitted by applicable law, Apple will have no other warranty obligation whatsoever with respect to the iOS App and will not be responsible for any other claims, losses, liabilities, damages, costs or expenses attributable to any failure to conform to any warranty. Apple is not responsible for addressing your or any third-party claims relating to an iOS App and/or use of an iOS App, including, but not limited to: (i) product liability claims; (ii) any claim that an iOS App fails to conform to any applicable legal or regulatory requirement; and (iii) claims arising under consumer protection or similar legislation. It is not the intent of these Terms to limit Tempus’ liability beyond what is permitted by applicable law. In the event of any third party claim that an iOS App or your possession and use (in accordance with these Terms) of an iOS App infringes that third party's intellectual property rights, Apple will not be responsible for the investigation, defense, settlement and discharge of any such intellectual property infringement claim. You represent and warrant that (i) you are not located in a country that is subject to a U.S. Government embargo, or that has been designated by the U.S. Government as a "terrorist supporting" country; and (ii) you are not listed on any U.S. Government list of prohibited or restricted parties. Apple, and Apple's subsidiaries, are third-party beneficiaries of these Terms with respect to any iOS App, and that, upon your acceptance of these Terms, Apple has the right (and deemed to have accepted the right) to enforce the Terms against you with respect to the iOS App as a third-party beneficiary thereof. Please direct any questions, complaints or claims with respect to an iOS App to Tempus at the contact information provided below.`,
        },
      ],
    },
    {
      paragraph: [
        {
          text: '17.\tDispute Resolution.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Please read the following sections carefully because they require you to arbitrate certain disputes, claims, suits, actions, causes of action, demands or proceedings against Tempus that in any way arise out of or relate to the operation, or your use, of The App ("Disputes") and limit the manner in which you can seek relief from us in such Disputes. For the avoidance of doubt, "Disputes" under this section does not include medical disputes, claims, suits, actions, causes of action, demands or proceedings that arise out of Test results by Tempus, even if the such dispute, claim, suit, action, cause of action, demand, or proceeding  involves The App.',
          styles: [Styles.bold],
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'You agree that regardless of any statute or law to the contrary, any Dispute brought by you must be filed within one (1) year after such claim or cause of action arose, and that thereafter such claim or cause of action will forever be barred. You and Tempus agree to resolve disputes only on an individual basis, through arbitration pursuant to the sub-sections below. ',
        },
      ],
    },

    {
      paragraph: [
        {
          text:
            'You agree that, by agreeing to these Terms, the U.S. Federal Arbitration Act governs the interpretation and enforcement of this provision, and that you and Tempus are each waiving the right to a trial by jury or to participate in a class action. This arbitration provision shall survive termination of these Terms and any other contractual relationship between you and Tempus.',
        },
      ],
      indent: 15,
    },

    {
      paragraph: [
        {
          text:
            'You may decline this agreement to arbitrate by notifying Tempus in writing within thirty (30) days of first accepting these Terms to the following address: 600 W. Chicago Avenue, Chicago, IL 60654, Attn: Legal Department (“',
        },

        {
          text: 'Notice Address',
          styles: [Styles.bold],
        },

        {
          text: '”).',
        },
      ],
      indent: 15,
    },
    {
      paragraph: [
        {
          text:
            'If you desire to assert a claim against Tempus, and you therefore elect to seek arbitration, you must first send to Tempus, by certified mail, a written notice of your claim (“',
        },

        {
          text: 'Claim Notice',
          styles: [Styles.bold],
        },

        {
          text:
            '”). The Claim Notice to Tempus should be addressed to the Notice Address. If Tempus desires to assert a claim against you and therefore elects to seek arbitration, it will send, by certified mail, a written Claim Notice to the most recent address we have on file or otherwise in our records for you. A Claim Notice, whether sent by you or by Tempus, must (a) describe the nature and basis of the claim or dispute; and (b) set forth the specific relief sought (“',
        },

        {
          text: 'Demand',
          styles: [Styles.bold],
        },

        {
          text:
            '”). If Tempus and you do not reach an agreement to resolve the claim within 30 days after the Claim Notice is received, you or Tempus may commence an arbitration proceeding or file a claim in small claims court. During the arbitration, the amount of any settlement offer made by Tempus or you shall not be disclosed to the arbitrator until after the arbitrator makes a final decision and award (if any). You may download or copy a form notice and a form to initiate arbitration from the American Arbitration Association at ',
        },
        {
          text: 'www.adr.org.',
          selectable: true,
        },
      ],
      indent: 15,
    },

    {
      paragraph: [
        {
          text:
            'Payment of all filing, administration and arbitrator fees will be governed by the AAA Rules. If, however, you are able to demonstrate that the costs of arbitration will be prohibitive for you as compared to the costs of litigation, Tempus will pay as much of the filing fees as the arbitrator deems necessary to prevent the arbitration from being cost-prohibitive for you. If the arbitrator decides that either the substance of your claim or the remedy you asked for is frivolous or brought for an improper purpose, then Tempus will use the AAA Rules to determine whether you or Tempus is responsible for the filing, administrative and arbitrator fees.',
        },
      ],
      indent: 15,
    },

    {
      paragraph: [
        {
          text:
            'The arbitration will be governed by the Commercial Arbitration Rules and the Supplementary Procedures for Consumer Related Disputes (collectively, “',
        },

        {
          text: 'AAA Rules',
          styles: [Styles.bold],
        },
        {
          text: '”) of the American Arbitration Association (“',
        },

        {
          text: 'AAA',
          styles: [Styles.bold],
        },
        {
          text:
            '”), as modified by these Terms, and will be administered by the AAA. The AAA Rules and Forms are available online at www.adr.org, by calling the AAA at ',
        },
        {
          text: '1-800-778-7879',
          selectable: true,
        },
        {
          text:
            ", or by requesting them from us by writing to us at the Notice Address. The arbitrator is bound by the these Terms. All issues are for the arbitrator to decide, including issues relating to the scope and enforceability of these Terms, including this arbitration agreement. Unless Tempus and you agree otherwise, any arbitration hearing will take place in Chicago, IL, USA. If your claim is for US $10,000 or less, we agree that you may choose whether the arbitration will be conducted solely on the basis of documents submitted to the arbitrator, through a telephonic hearing, or by an in-person hearing as established by the AAA Rules. If your claim exceeds US $10,000, the right to a hearing will be determined by the AAA Rules. Regardless of the manner in which the arbitration is conducted, the arbitrator shall issue a reasoned written decision sufficient to explain the essential findings and conclusions on which the award is based. If the arbitrator issues you an award that is greater than the value of Tempus’ last written settlement offer made before an arbitrator was selected (or if Tempus did not make a settlement offer before an arbitrator was selected), then Tempus will pay you the amount of the award or US $1,000, whichever is greater. Except as expressly set forth herein, the payment of all filing, administration, and arbitrator fees will be governed by the AAA Rules. Each party shall pay for its own costs and attorneys' fees, if any. However, if any party prevails on a statutory claim that affords the prevailing party attorneys' fees, or if there is a written agreement providing for payment or recovery attorneys’ fees, the arbitrator may award reasonable fees to the prevailing party, under the standards for fee shifting provided by law.",
        },
      ],
      indent: 15,
    },

    {
      paragraph: [
        {
          text:
            "YOU EXPRESSLY WAIVE ANY RIGHT TO BRING ANY ACTION, LAWSUIT, OR PROCEEDING AS AN INDIVIDUAL, CLASS OR COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR ANY OTHER PROCEEDING BEFORE A COURT IN WHICH ANY PARTY ACTS OR PROPOSES TO ACT IN A REPRESENTATIVE CAPACITY.  YOUR RIGHTS WILL BE DETERMINED BY A NEUTRAL ARBITRATOR AND NOT JUDGE OR JURY.  YOU AND TEMPUS AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. Further, unless both you and Tempus agree otherwise, the arbitrator may not consolidate more than one person's claims with your claims, and may not otherwise preside over any form of a representative or class proceeding. The arbitrator may award declaratory or injunctive relief only in favor of the individual party seeking relief and only to the extent necessary to provide relief warranted by that party's individual claim.",
        },

        {
          text:
            'If this Dispute Resolution provision is found to be unenforceable, then (a) the entirety of this arbitration provision shall be null and void, but the remaining provisions of these Terms and Conditions of Use shall remain in full force and effect; and (b) exclusive jurisdiction and venue for any claims will be in state or federal courts located in and for Chicago, Illinois.',
        },
      ],
      indent: 15,
    },
    {
      paragraph: [
        {
          text: '18.\t',
        },
        {
          text: 'Conflict with AAA Rules.',
          styles: [Styles.underline],
        },
        {
          text: 'This Agreement governs if there is a conflict with the AAA’s Commercial Arbitration Rules.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '19.\t',
        },
        {
          text: 'Requirement to File Within One Year',
          styles: [Styles.underline],
        },
        {
          text:
            'Notwithstanding any other statute of limitations, a claim or dispute under this Agreement must be filed in Small Claims Court or noticed for arbitration within one year of when it could first be filed, or such claim will be permanently barred.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '20.\t',
        },
        {
          text: 'Export Compliance.',
          styles: [Styles.underline],
        },
        {
          text:
            "You may not use or otherwise export or re-export the The App except as authorized by United States law. In particular, but without limitation, the The App may not be exported or re-exported (i) into any U.S.-embargoed countries or (ii) to anyone on the U.S. Treasury Department's Specially Designated Nationals List or the U.S. Department of Commerce Denied Persons List or Entity List. By using the The App, you represent and warrant that you are not located in any such country or on any such list. You also agree that you will not use these products for any purposes prohibited by United States law, including, without limitation, the development, design, manufacture, or production of nuclear, missile, or chemical or biological weapons.",
        },
      ],
    },
    {
      paragraph: [
        {
          text: '21.\t',
        },
        {
          text: 'Force Majeure.',
          styles: [Styles.underline],
        },
        {
          text:
            'We will not be deemed to be in breach of these Terms or liable for any breach of these Terms or our Privacy Policies due to any event or occurrence beyond our reasonable control, including without limitation, acts of God, terrorism, war, invasion, failures of any public networks, electrical shortages, earthquakes or floods, civil disorder, strikes, fire, or other disaster.',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '22.\t',
        },
        {
          text: 'Entire Agreement.',
          styles: [Styles.underline],
        },
        {
          text:
            'These Terms, including any documents incorporated into these Terms by reference, constitute the entire agreement between you and us regarding the subject matter of these Terms and supersede all prior agreements and understandings, whether written or oral, or whether established by custom, practice, policy or precedent, with respect to the subject matter of the Terms. If any provision of the Terms is found to be illegal, void, or unenforceable, the unenforceable provision will be modified so as to render it enforceable to the maximum extent possible in order to effect the intention of the provision; if a term cannot be so modified, it will be severed and the remaining provisions of the Terms will not be affected in any way. Our failure to enforce a provision is not a waiver of our right to do so later.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: '23.\t',
        },
        {
          text: 'Assignment.',
          styles: [Styles.underline],
        },
        {
          text:
            'TYou may not assign, transfer, or delegate the Terms or any part thereof without Tempus’ prior written consent. Tempus may freely transfer, assign, or delegate all or any part of the Terms, and any rights or duties hereunder or thereunder. ',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '24.\t',
        },
        {
          text: 'No Waiver.',
          styles: [Styles.underline],
        },
        {
          text:
            'The failure of Tempus to insist upon strict enforcement of any provision of these Terms & Conditions of Use shall not be construed as a waiver of any provision or right.',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '25.\t',
        },
        {
          text: 'No Third Party Beneficiaries.',
          styles: [Styles.underline],
        },
        {
          text:
            'Unless expressly stated in the Terms and Conditions of Use to the contrary, nothing herein is intended to confer any rights or remedies on any persons other than you, Tempus, and Tempus Affiliates. Nothing in the Terms and Conditions of Use is intended to relieve or discharge the obligation or liability of any third persons to you, Tempus, and its affiliates, nor shall any provision give any third parties any right of subrogation or action over against you, Tempus, and its affiliates',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '26.\t',
        },
        {
          text: 'Jurisdiction.',
          styles: [Styles.underline],
        },
        {
          text:
            'These Terms and the relationship between you and Tempus shall be governed by the laws of the State of Illinois, excluding its conflicts of law provisions.',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '27.\t',
        },
        {
          text: 'Right to Change.',
          styles: [Styles.underline],
        },
        {
          text:
            'Tempus may, at any time and from time to time, change the Terms. Any changes to these Terms will be effective immediately upon posting of the changed Terms, which may be found on The App. If Tempus makes any changes to the Dispute Resolution terms (other than a change to Tempus’ address for notice), you may reject any of those changes by notifying Tempus via the process set forth in Section 17 within 30 days of the change. By rejecting a future change, you are agreeing to arbitrate any dispute between us in accordance with the language of the last Dispute Resolution terms that you accepted',
        },
      ],
    },

    {
      paragraph: [
        {
          text: '28.\t',
        },
        {
          text: 'Copyright/Trademark Information.',
          styles: [Styles.underline],
        },
        {
          text: 'Copyright ©2020 Tempus Labs, Inc.. All rights reserved. All trademarks, logos, and service marks (“',
        },
        {
          text: 'Mark',
          styles: [Styles.bold],
        },

        {
          text:
            '”) displayed on The App are our property or the property of other third parties. You are not permitted to use these Marks without our prior written consent or the consent of such third party which may own the Marks.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'How to Contact Us:',
          styles: [Styles.bold],
        },
      ],
      justify: textJustificationTypes.CENTER,
      singleSpace: true,
    },
    {
      paragraph: [
        {
          text: 'Tempus Labs, Inc.',
        },
      ],
      justify: textJustificationTypes.CENTER,
      singleSpace: true,
    },
    {
      paragraph: [
        {
          text: '600 W. Chicago Ave.',
        },
      ],
      justify: textJustificationTypes.CENTER,
      singleSpace: true,
    },
    {
      paragraph: [
        {
          text: 'Chicago, IL 60654',
        },
      ],
      justify: textJustificationTypes.CENTER,
      singleSpace: true,
    },
    {
      paragraph: [
        {
          text: 'support@tempus.com',
        },
      ],
      justify: textJustificationTypes.CENTER,
    },
  ],
  hipaaAuthorization: [
    {
      paragraph: [
        {
          text: 'Tempus is working to make precision medicine a reality for COVID-19 patients, and we need your help.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'Each person’s infection and medical circumstances are unique. Tempus is creating a database of de-identified health information from many COVID-19 patients. We think we can improve infectious disease care and research, in the United States and abroad, by adding more de-identified health information to our database.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'By signing below, you give Tempus permission to access medical records from your health care providers. We will remove your name, date of birth, home address, e-mail address, and other identifying information from those records, following de-identification rules from a federal privacy law called HIPAA. We will store the resulting de-identified health information in our database.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'We use our database of de-identified health information for many different purposes. We use it to improve our own internal laboratory operations. We share information from it with others, like universities, hospitals, and companies, who research the infectious disease, develop new drugs and therapies, and help pay for the cost of health care. We may receive payment when sharing the information. Your de-identified health information may be in some of the information that we use or share.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'We follow the law when we use or disclose the information that we get from your authorization. We will never use the consent you provide here to information to market products or services to you, or to contact you by phone or e-mail. If you want to give us permission to access your medical records, please sign below.',
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'Consent and Authorization for Provider to Release Health Information',
          styles: [Styles.bold],
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'I authorize all hospitals, physicians, medical practices, medical record departments, and other health care providers who have been or in the future will be involved in my health care (each a “Provider”) to disclose my entire health record electronically or by hard copy to Tempus Labs, Inc.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'This authorization is made at my own request, for the purposes described above. It will expire ten (10) years after the date of signature below. This authorization is for the health record that exists as of the date I sign this authorization. It also is for any new health records that may be created after the date I sign this authorization.  This authorization does not permit disclosure of psychotherapy notes. If my health record includes genetic information or information related to communicable diseases (including HIV or AIDS), behavioral/mental health, or substance abuse treatment, I permit this information to be disclosed to Tempus. If a Provider cannot release this type of information, then I authorize the Provider to release a copy of my health record with this information removed.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'I understand that: 1) my health records may be redisclosed by Tempus to other individuals or institutions and may no longer be protected by HIPAA; 2) I may refuse to sign this authorization; 3) my refusal to sign will not affect my ability to obtain treatment or benefits from Tempus or any Provider; 4) e-mail may be sent through unencrypted methods and I am aware of the possible risks of using unencrypted e-mail; and 5) I have the right to inspect and copy the information to be disclosed pursuant to this authorization and that I may obtain a copy of the information by contacting Tempus. I may take back (“revoke”) this authorization in writing at any time by sending written notification to Tempus at the address listed above, to the attention of the Chief Privacy Officer. Tempus will then notify each applicable Provider in accordance with that Provider’s usual practices for receiving revocations. I understand that any revocation will not apply to authorized uses or disclosures of my medical record that occurred prior to the date Tempus receives my written request to revoke authorization.',
        },
      ],
    },
    {
      paragraph: [
        {
          text:
            'A copy of this signed authorization will be sent to the e-mail address listed below. I confirm that this is my personal e-mail address.',
          styles: [Styles.bold],
        },
      ],
    },
    {
      paragraph: [
        {
          text: 'I have read and understood this form and authorize the release of my protected health information.',
          styles: [Styles.bold],
        },
      ],
    },
  ],
  primaryCarePhysician: {
    paragraph: [
      {
        text:
          'Please provide your primary care physician contact information below. Tempus will contact them to securely transfer your files.',
      },
    ],
  },
  assistance: [
    {
      paragraph: [
        {
          text: 'Questions about your results?',
          styles: [Styles.bold],
        },
      ],
    },
    { paragraph: [{ text: 'Please reach out to your healthcare provider' }] },
    { paragraph: [{ text: '' }] },
    { paragraph: [{ text: '' }] },
    {
      paragraph: [
        {
          text: 'Feedback or technical issue with your mobile app?',
          styles: [Styles.bold],
        },
      ],
    },

    {
      paragraph: [
        {
          text: 'Please email Tempus at ',
        },
        {
          text: CONTACT_US_EMAIL,
          selectable: true,
        },
      ],
    },
  ],
  privacyConfirmationText: [
    {
      paragraph: [
        {
          text:
            'Tempus would like permission to collect data on your use of this application to improve our patient experience.',
        },
      ],
    },
  ],
};

export default currentContent;
